import _ from 'lodash'
import React from 'react'

import { SearchMenu } from '../SearchMenu'
import { useSessionStorage } from '../../hooks/useSessionStorage'
import { ContactWidget } from '../ContactWidget/ContactWidget'

import { Header } from './Header'
import { LoginAsHeader } from './LoginAsHeader'
import { NavMenu, NavigationLink } from './NavMenu'
import { PageBanner, PageBannerProps } from './PageBanner'
import { StickyWrapper } from './StickyWrapper'

export type RouteButtons = 'Archive' | 'Profile' | 'BulkImport'

type Props = {
  navMenuItems: NavigationLink[]
  countryFilterEnabled: boolean
  defaultCountryFilter?: {
    label: string
    countryCode: string
  }
  otherCountriesFilter?: {
    label: string
    countryCode: string
  }[]
  searchOptions: {
    label: string
    identifier: string
    placeholder?: string
    countries?: {
      label: string
      countryCode: string
    }[]
  }[]
  visible: boolean
  homeUrl: string
  ref: React.MutableRefObject<any>
  searchWidgetDisplayMode?: 'WithFilters' | 'WithoutFilters'
  cmsFaqUrl: string
  loggedIn: boolean
  userName: string
  fullName: string
  logoUrl: {
    url: string
    alt?: string
  }
  countOfOrderItems: number
  loginUrl: string
  showBulkImportMenuInHeader: boolean | undefined
  showBulkImportLinkAboveSearchButton: boolean | undefined
  showAsBulkImportButton: boolean | undefined
  showMulticolumnDropdown: boolean | undefined
  countUnreadNotifications: number
  selectedMenuItem: string | undefined
  logout: () => void
  loginAsLogout: () => void
  activeButton: RouteButtons | undefined
  links: {
    Archive: React.FunctionComponent
    Profile: React.FunctionComponent
    Bulk: React.FunctionComponent
    Cart: React.FunctionComponent
    Notifications: React.FunctionComponent
  }
  handleMarkAllNotificationsReadClick: () => void
  queryString: string
  onSearchClick: (params: { query: string; filter: string; countryFilter?: string }) => void
  changeLanguage: (lng: string) => void
  hasBriqBulkImportRole: boolean
  ImportHistoryLink: React.FunctionComponent
  toggle: (name: string) => void
  close: (name: string) => void
  contactWidget:
    | {
        id: string
        title: string
        description: string
        link?: {
          url: string
          text: string
          target?: string
        }
        textColor: string
        image?: {
          url: string
          alt?: string
        }
        backgroundColor?: string
        icon?: string
      }
    | undefined
  showAiChatbotButton: boolean
  toggleAiChatbotWidget: () => void
} & PageBannerProps

const StickyPageHeaderComponent = (
  {
    homeUrl,
    navMenuItems,
    searchOptions,
    visible,
    searchWidgetDisplayMode,
    countryFilterEnabled,
    defaultCountryFilter,
    otherCountriesFilter,
    cmsFaqUrl,
    loggedIn,
    userName,
    countOfOrderItems,
    fullName,
    loginUrl,
    logoUrl,
    showBulkImportMenuInHeader,
    countUnreadNotifications,
    showAsBulkImportButton,
    showBulkImportLinkAboveSearchButton,
    selectedMenuItem,
    logout,
    loginAsLogout,
    activeButton,
    links,
    handleMarkAllNotificationsReadClick,
    queryString,
    onSearchClick,
    changeLanguage,
    hasBriqBulkImportRole,
    ImportHistoryLink,
    toggle,
    close,
    showMulticolumnDropdown,
    contactWidget,
    showAiChatbotButton,
    toggleAiChatbotWidget,
    ...rest
  }: Props,
  ref: any
) => {
  const [loginAsValue] = useSessionStorage('isLoginAs', '')
  const isLoginAs = loginAsValue === 'true'

  return (
    <StickyWrapper ref={ref} visible={visible} position="fixed">
      <PageBanner {...rest} />
      {isLoginAs && <LoginAsHeader loggedIn={loggedIn} fullName={userName} logout={loginAsLogout} />}
      <Header
        py={1}
        homeUrl={homeUrl}
        changeLanguage={changeLanguage}
        logout={logout}
        activeButton={activeButton}
        cmsFaqUrl={cmsFaqUrl}
        countOfOrderItems={countOfOrderItems}
        fullName={fullName}
        loggedIn={loggedIn}
        loginUrl={loginUrl}
        logoUrl={logoUrl}
        showBulkImportMenuInHeader={showBulkImportMenuInHeader}
        links={links}
        countUnreadNotifications={countUnreadNotifications}
        handleMarkAllNotificationsReadClick={handleMarkAllNotificationsReadClick}
        showAiChatbotButton={showAiChatbotButton}
        toggleAiChatbotWidget={toggleAiChatbotWidget}
      />
      <SearchMenu
        searchOptions={searchOptions}
        searchWidgetDisplayMode={searchWidgetDisplayMode}
        countryFilterEnabled={countryFilterEnabled}
        defaultCountryFilter={defaultCountryFilter}
        otherCountriesFilter={otherCountriesFilter}
        sticky
        onSearchClick={onSearchClick}
        queryString={queryString}
        loggedIn={loggedIn}
        hasBriqBulkImportRole={hasBriqBulkImportRole}
        showAsBulkImportButton={showAsBulkImportButton}
        showBulkImportLinkAboveSearchButton={showBulkImportLinkAboveSearchButton}
        ImportHistoryLink={ImportHistoryLink}
        showMulticolumnDropdown={showMulticolumnDropdown}
      />
      <NavMenu
        variant="primaryDark"
        items={navMenuItems}
        toggle={toggle}
        close={close}
        isVisible={name => name === selectedMenuItem}
      />
      {contactWidget && _.includes(contactWidget?.icon, 'top') && (
        <ContactWidget contactWidget={contactWidget} anchor={true} />
      )}
    </StickyWrapper>
  )
}

export const StickyPageHeader = React.forwardRef(StickyPageHeaderComponent)
export default StickyPageHeader
